import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import TabSelectionHeader from "@features/common/header/tabSelectionHeader/TabSelectionHeader";
import Notifier from "@features/common/notifier/Notifier";
import {
  useFetchDashboardQuery,
  useFetchProfileQuery,
} from "@features/common/commonApi";
import PageLoader from "@features/common/pageLoader/PageLoader";
import { useAppDispatch } from "@redux/hooks";
import { setDashboardData, setProfileData } from "@redux/slices/CommonSlice";
import { PAGE_URLS } from "@constants/common";
import ChatWidget from "@features/common/chatWidget/ChatWidget";
import NoInternet from "@features/common/noInternet/NoInternet";

const CommonLayout: React.FC = () => {
  /* --------------------------------------------------------------------------
   * Hooks
   * -------------------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const location = useLocation();

  // No Internet logic
  const [hasInternet, setHasInternet] = useState(window.navigator.onLine);

  useEffect(() => {
    const updateNetwork = () => setHasInternet(window.navigator.onLine);
    window.addEventListener("offline", updateNetwork);
    window.addEventListener("online", updateNetwork);
    return () => {
      window.removeEventListener("offline", updateNetwork);
      window.removeEventListener("online", updateNetwork);
    };
  }, []);

  /* --------------------------------------------------------------------------
   * API Queries
   * -------------------------------------------------------------------------- */
  /**
   * Fetch user profile data first
   * This is called before dashboard to ensure user data is loaded
   */
  const {
    data: profileData,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useFetchProfileQuery();

  /**
   * Fetch dashboard data containing corporate balance and name
   * Skipped when user is on select corporate page or while profile is loading
   */
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    refetch: refetchDashboard,
  } = useFetchDashboardQuery();

  /* --------------------------------------------------------------------------
   * Effects
   * -------------------------------------------------------------------------- */
  useEffect(() => {
    refetchProfile();
    refetchDashboard();
  }, [location.pathname]);
  /**
   * Store profile data in Redux state when fetched
   * Extracts user information and stores it globally
   */
  useEffect(() => {
    if (profileData) {
      dispatch(
        setProfileData({
          corporate_name: profileData?.data?.corporate_name || "",
          email: profileData?.data?.email || "",
          full_name: profileData?.data?.full_name || "",
          first_name: profileData?.data?.first_name || "",
          last_name: profileData?.data?.last_name || "",
        }),
      );
    }
  }, [profileData, dispatch]);

  /**
   * Store dashboard data in Redux state when fetched
   * Extracts corporate balance (amount & currency) and corporate name
   * Provides default values if data is missing
   */
  useEffect(() => {
    if (dashboardData) {
      dispatch(
        setDashboardData({
          amount: dashboardData?.data?.corporate_balance?.amount || 0,
          currency: dashboardData?.data?.corporate_balance?.currency || "",
          corporateName: dashboardData?.data?.name || "",
          logoUrl: dashboardData?.data?.logo || "",
          showCorporateSwitch:
            dashboardData?.data?.show_corporate_switching || false,
        }),
      );
    }
  }, [dashboardData, dispatch]);

  /* --------------------------------------------------------------------------
   * Loading State
   * -------------------------------------------------------------------------- */
  /** Show full-page loader while fetching initial profile or dashboard data */
  if (isProfileLoading || isDashboardLoading) return <PageLoader />;

  /* --------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------- */
  return (
    <>
      {/* No Internet Banner */}
      <NoInternet isOnline={hasInternet} />
      {/* Tab Navigation Header */}
      <TabSelectionHeader>
        {/* Toast Notifications */}
        <Notifier />

        {/* Child Routes */}
        <Outlet />
        <ChatWidget />
      </TabSelectionHeader>
    </>
  );
};

export default CommonLayout;
